import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Newspaper } from 'lucide-react';
import newsService, { NewsArticle } from '../../services/newsService';
import ThumbnailImage from '../ThumbnailImage';
import DynamicSlider from '../slider/DynamicSlider';

/** Slider-Location (Web + App an derselben Stelle, Marcel 2026-09-15): unter dem Hauptartikel */
export const NEWS_LEAD_SLIDER_LOCATION = 'home_news_lead';

const ACCENT = '#3fced1';

const articleHref = (item?: NewsArticle) => (item?.slug ? `/news/${item.slug}` : '#');

/**
 * Aufmacher-Bereich im Zeitungslayout (statt Hero): eine große Lead-News-Karte
 * links (Bild + Gradient-Overlay + Kategorie-Badge) und rechts 4 kompakte
 * Sekundär-News mit Thumbnail. Blendet sich aus, wenn keine News da sind.
 *
 * CLS-Fix (2026-09-01): solange die News LADEN, reserviert ein Skeleton im
 * identischen Grid-Layout (Lead-Karte in Endhöhe + 4 Sekundär-Zeilen) den
 * Platz — News sind der Normalfall auf der Startseite.
 */
const LeadStoryGrid: React.FC<{ articles: NewsArticle[]; loading?: boolean }> = ({
  articles,
  loading = false,
}) => {
  if (articles.length === 0) {
    if (!loading) return null;
    return (
      <section aria-label="Top News" aria-hidden="true">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="section-heading text-base font-bold text-white">News</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
          {/* Lead-Karte: gleiche min-h wie die echte Karte */}
          <div className="min-h-[300px] animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900 sm:min-h-[380px] lg:min-h-[420px]" />
          {/* 4 Sekundär-Zeilen mit identischer Struktur (Bild h-16 + p-3) */}
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-1 items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-900 p-3"
              >
                <div className="h-16 w-24 shrink-0 animate-pulse rounded-lg bg-zinc-800" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-3.5 w-4/5 animate-pulse rounded bg-zinc-800" />
                  <div className="h-3 w-2/5 animate-pulse rounded bg-zinc-800" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const lead = articles[0];
  const secondary = articles.slice(1, 5);
  const leadImage = newsService.getImageUrl(lead);
  const leadSource = newsService.getDisplaySource(lead);

  return (
    <section aria-label="Top News">
      {/* Section-Header wie in der App: links Titel, rechts "Alle News" */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">News</h2>
        <Link
          to="/news"
          className="flex items-center gap-1 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={{ color: ACCENT }}
        >
          Alle News
          <ArrowRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
        {/* Lead-Story */}
        <Link to={articleHref(lead)} className="group block">
          <article className="relative h-full min-h-[300px] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 sm:min-h-[380px] lg:min-h-[420px]">
            {leadImage ? (
              <img
                src={leadImage}
                alt={newsService.formatTitle(lead)}
                // LCP-Element der Startseite: sofort + mit hoher Priorität laden
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
                <Newspaper size={48} className="text-zinc-700" />
              </div>
            )}
            {/* dc-on-image: Overlay bleibt im Light Mode dunkel, Text hell (2026-09-02) */}
            <div className="dc-on-image absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="dc-on-image absolute inset-x-0 bottom-0 p-5 sm:p-6">
              {lead.category && (
                <span
                  className="mb-2 inline-flex rounded px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-black"
                  style={{ backgroundColor: ACCENT }}
                >
                  {lead.category}
                </span>
              )}
              <h2 className="line-clamp-3 text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl">
                {newsService.formatTitle(lead)}
              </h2>
              <div className="mt-3 flex items-center gap-3 text-xs text-gray-400 sm:text-sm">
                <span className="flex items-center gap-1.5">
                  <Clock size={13} />
                  {newsService.formatArticleTime(lead)}
                </span>
                {leadSource && <span className="truncate">{leadSource}</span>}
              </div>
            </div>
          </article>
        </Link>

        {/* Rechte Spalte: Slider (Mobile direkt unter dem Hauptartikel, Desktop
            rechts neben ihm über den Nebenartikeln – Marcel 2026-09-15), dann Sekundär-News */}
        <div className="flex flex-col gap-3">
          <DynamicSlider location={NEWS_LEAD_SLIDER_LOCATION} className="w-full" />
          {secondary.map((item) => (
            <SecondaryStory key={item._id || item.id || item.slug} article={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

const SecondaryStory: React.FC<{ article: NewsArticle }> = ({ article }) => {
  const image = newsService.getImageUrl(article);
  return (
    <Link
      to={articleHref(article)}
      className="group flex flex-1 items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-900 p-3 transition-colors hover:border-[#3fced1]/50"
    >
      {image ? (
        // Kleine Karte (96×64px) → 200er-Thumbnail statt Original-Poster
        <ThumbnailImage
          src={image}
          thumbSize={200}
          alt=""
          loading="lazy"
          className="h-16 w-24 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
          <Newspaper size={18} className="text-zinc-600" />
        </div>
      )}
      <div className="min-w-0">
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {newsService.formatTitle(article)}
        </h3>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
          <span>{newsService.formatArticleTime(article)}</span>
          {article.category && <span className="truncate">{article.category}</span>}
        </div>
      </div>
    </Link>
  );
};

export default LeadStoryGrid;
